import React, { FC, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useStoreService } from '@goparrot-dashboard-core/store-service';
import { IconArrowDownThin } from '@goparrot-dashboard/shared-ui';
import classNames from 'classnames';
import { useHistory } from 'react-router';
import { useSquareNav } from '@goparrot-dashboard/shared-utils';
import { MenuItem } from '../../interface';

type SidebarGroupProps = {
  title: string;
  hideDropdownEffect?: boolean;
  className?: string;
  menuItems?: MenuItem[];
};

export const SidebarGroup: FC<React.PropsWithChildren<SidebarGroupProps>> = ({ children, title, menuItems, hideDropdownEffect = false, className }) => {
  const history = useHistory();
  const { isMerchant } = useStoreService();
  const isSquareNavEnabled = useSquareNav();
  const [isOpen, setIsOpen] = useState(!isSquareNavEnabled);
  const nodeRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (nodeRef.current) nodeRef.current.style.height = 'auto';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  useEffect(() => {
    if (!isSquareNavEnabled) {
      return;
    }

    const dashboardPathSegments = history.location.pathname.split('/');
    const isCurrentGroupSelected =
      menuItems?.some((subItem) => {
        const subitemSegments = subItem.link.split('/');
        const idIndex = subitemSegments.findIndex((v) => v === ':id');
        const defaultIndex = isSquareNavEnabled ? 1 : 2;
        const segmentIndex = isMerchant || idIndex === -1 ? defaultIndex : idIndex + 1;
        return subitemSegments[segmentIndex] === dashboardPathSegments[segmentIndex];
      }) ?? false;
    setIsOpen(isCurrentGroupSelected);
  }, [history.location.pathname, menuItems, isMerchant, isSquareNavEnabled]);

  const onTriggerDropdownEffect = () => {
    !hideDropdownEffect ? setIsOpen(!isOpen) : null;
  };

  const setToScrollHeight = () => {
    if (nodeRef.current) nodeRef.current.style.height = `${nodeRef.current.scrollHeight}px`;
  };

  const onExpandEnd = () => {
    if (nodeRef.current) nodeRef.current.style.height = 'auto';

    containerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  };

  const setHeightZero = () => {
    if (nodeRef.current) nodeRef.current.style.height = '0';
  };

  return (
    <div className={className} ref={containerRef}>
      <div
        data-testid={`${title.replace(/ /, '-').toLowerCase()}-sidebar`}
        onClick={onTriggerDropdownEffect}
        className={classNames([
          'tw-cursor-pointer semibold-30 tw-tracking-tight',
          isSquareNavEnabled ? 'tw-px-2 tw-justify-between tw-flex tw-items-center tw-py-3' : 'tw-pl-1.5 tw-mb-2',
        ])}
      >
        {title}
        {!hideDropdownEffect && (
          <IconArrowDownThin
            className={classNames([
              'tw-ml-1.5 tw-mb-0.5 tw-transition-all tw-ease-in-out tw-transform',
              !isOpen && 'tw--rotate-90',
              isSquareNavEnabled && 'tw-w-3 tw-h-3',
            ])}
          />
        )}
      </div>
      <CSSTransition
        timeout={200}
        in={isOpen}
        unmountOnExit
        appear
        nodeRef={nodeRef}
        classNames="tw-overflow-hidden tw-transition-all tw-ease-in-out"
        onEnter={setToScrollHeight}
        onEntered={isSquareNavEnabled ? onExpandEnd : undefined}
        onExit={setToScrollHeight}
        onExiting={setHeightZero}
      >
        <div ref={nodeRef} style={{ height: 0 }}>
          {children}
        </div>
      </CSSTransition>
    </div>
  );
};
